/**
 * Eufy 体重計データの推移グラフページ
 */

import { format, parseISO, subDays } from 'date-fns'
import { ja } from 'date-fns/locale'
import { useEffect, useMemo, useState } from 'react'
import ReactSelect, { type MultiValue, type StylesConfig } from 'react-select'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { getBodyProfile } from '@/api/services/nutrition'
import type { EufyMeasurementItem } from '@/api/types/eufy'
import { Header } from '@/components/shared/header'
import { Card } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useGoBack } from '@/hooks/useGoBack'
import { BfProjectionChart } from '@/pages/eufy/components/bf-projection-chart'
import { useEufyStore } from '@/store/useEufyStore'

type RangeOption = { label: string; days: number }

const RANGES: RangeOption[] = [
  { label: '7日', days: 7 },
  { label: '30日', days: 30 },
  { label: '90日', days: 90 },
  { label: '1年', days: 365 },
]

type MetricKey =
  | keyof Pick<
      EufyMeasurementItem,
      | 'weight_kg'
      | 'body_fat'
      | 'muscle_mass'
      | 'skeletal_muscle_mass'
      | 'bone_mass'
      | 'water'
      | 'visceral_fat'
      | 'bmi'
      | 'bmr'
      | 'protein_ratio'
      | 'body_age'
    >
  | 'lbm'

const METRICS: { key: MetricKey; label: string; unit: string; color: string }[] = [
  { key: 'weight_kg', label: '体重', unit: 'kg', color: 'hsl(221 83% 53%)' },
  { key: 'body_fat', label: '体脂肪率', unit: '%', color: 'hsl(25 95% 53%)' },
  { key: 'muscle_mass', label: '筋肉量', unit: 'kg', color: 'hsl(142 76% 36%)' },
  { key: 'skeletal_muscle_mass', label: '骨格筋量', unit: 'kg', color: 'hsl(142 60% 50%)' },
  { key: 'bone_mass', label: '骨量', unit: 'kg', color: 'hsl(280 50% 50%)' },
  { key: 'water', label: '水分率', unit: '%', color: 'hsl(195 80% 50%)' },
  { key: 'visceral_fat', label: '内臓脂肪', unit: 'lvl', color: 'hsl(0 70% 50%)' },
  { key: 'bmi', label: 'BMI', unit: '', color: 'hsl(45 85% 50%)' },
  { key: 'bmr', label: '基礎代謝', unit: 'kcal', color: 'hsl(15 80% 55%)' },
  { key: 'protein_ratio', label: 'タンパク質', unit: '%', color: 'hsl(330 70% 50%)' },
  { key: 'body_age', label: '体内年齢', unit: '歳', color: 'hsl(210 30% 40%)' },
  { key: 'lbm', label: '除脂肪体重', unit: 'kg', color: 'hsl(160 70% 40%)' },
]

type MetricOption = { label: string; value: MetricKey; color: string }

const METRIC_OPTIONS: MetricOption[] = METRICS.map((m) => ({ label: m.label, value: m.key, color: m.color }))

const DEFAULT_SELECTED: MetricOption[] = METRIC_OPTIONS.filter((o) =>
  (['weight_kg', 'body_fat', 'muscle_mass'] as MetricKey[]).includes(o.value),
)

const metricSelectStyles: StylesConfig<MetricOption, true> = {
  control: (base) => ({
    ...base,
    backgroundColor: 'var(--card)',
    borderColor: 'var(--border)',
    minHeight: 32,
    fontSize: 12,
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--popover)',
    border: '1px solid var(--border)',
    fontSize: 12,
    zIndex: 50,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 50 }),
  menuList: (base) => ({
    ...base,
    maxHeight: 200,
  }),
  option: (base, { isFocused }) => ({
    ...base,
    backgroundColor: isFocused ? 'var(--accent)' : 'var(--popover)',
    color: 'var(--popover-foreground)',
    padding: '4px 8px',
  }),
  multiValue: (base, { data }) => ({
    ...base,
    backgroundColor: `color-mix(in srgb, ${data.color} 15%, transparent)`,
    borderRadius: 4,
  }),
  multiValueLabel: (base, { data }) => ({
    ...base,
    color: data.color,
    fontSize: 11,
    padding: '0 4px',
  }),
  multiValueRemove: (base, { data }) => ({
    ...base,
    color: data.color,
    ':hover': { backgroundColor: data.color, color: '#fff' },
  }),
  input: (base) => ({ ...base, color: 'var(--foreground)' }),
  placeholder: (base) => ({ ...base, color: 'var(--muted-foreground)' }),
}

const formatTick = (iso: string) => format(parseISO(iso), 'M/d', { locale: ja })

const computeMovingAverage = (values: (number | null)[], window: number): (number | null)[] => {
  return values.map((_, i) => {
    const start = Math.max(0, i - window + 1)
    const slice = values.slice(start, i + 1).filter((v): v is number => v !== null)
    return slice.length > 0 ? slice.reduce((a, b) => a + b, 0) / slice.length : null
  })
}

// ---------------------------------------------------------------------------
// オーバーレイグラフ: 複数メトリクスを重ねて表示
// ---------------------------------------------------------------------------

/** 各メトリクスの値を 0-100% に正規化して重ねる */
const OverlayChart = ({
  sortedAsc,
  metrics,
  showMa7,
  showMa14,
}: {
  sortedAsc: EufyMeasurementItem[]
  metrics: typeof METRICS
  showMa7: boolean
  showMa14: boolean
}) => {
  const [selectedOptions, setSelectedOptions] = useState<MetricOption[]>(DEFAULT_SELECTED)

  const selected = useMemo(() => new Set(selectedOptions.map((o) => o.value)), [selectedOptions])

  // 正規化データを構築
  const { chartData, ranges } = useMemo(() => {
    // 派生データの事前計算
    const getDerivedValue = (row: EufyMeasurementItem, key: MetricKey): number | null => {
      if (key === 'lbm') {
        const w = row.weight_kg as number | null
        const bf = row.body_fat as number | null
        return w !== null && bf !== null ? w * (1 - bf / 100) : null
      }
      return row[key] as number | null
    }

    // メトリクスごとの raw 値配列を事前計算
    const rawValuesMap: Record<string, (number | null)[]> = {}
    for (const m of metrics) {
      rawValuesMap[m.key] = sortedAsc.map((row) => getDerivedValue(row, m.key))
    }

    // メトリクスごとの移動平均
    const ma7Map: Record<string, (number | null)[]> = {}
    const ma14Map: Record<string, (number | null)[]> = {}
    if (showMa7) {
      for (const m of metrics) {
        ma7Map[m.key] = computeMovingAverage(rawValuesMap[m.key], 7)
      }
    }
    if (showMa14) {
      for (const m of metrics) {
        ma14Map[m.key] = computeMovingAverage(rawValuesMap[m.key], 14)
      }
    }

    // メトリクスごとの min/max（MA値も含めて正規化レンジを決定）
    const rangeMap: Record<string, { min: number; max: number }> = {}
    for (const m of metrics) {
      const allVals = [
        ...rawValuesMap[m.key],
        ...(showMa7 ? ma7Map[m.key] : []),
        ...(showMa14 ? ma14Map[m.key] : []),
      ].filter((v): v is number => v !== null)
      if (allVals.length > 0) {
        rangeMap[m.key] = { min: Math.min(...allVals), max: Math.max(...allVals) }
      }
    }

    const normalize = (val: number | null, key: string): number | null => {
      if (val === null || !rangeMap[key]) return null
      const { min, max } = rangeMap[key]
      const span = max - min
      return span > 0 ? ((val - min) / span) * 100 : 50
    }

    const rows = sortedAsc.map((_, i) => {
      const entry: Record<string, string | number | null> = {
        date: sortedAsc[i].measured_at as string,
      }
      for (const m of metrics) {
        const raw = rawValuesMap[m.key][i]
        entry[`raw_${m.key}`] = raw
        entry[m.key] = normalize(raw, m.key)
        if (showMa7 && ma7Map[m.key]) {
          entry[`raw_ma7_${m.key}`] = ma7Map[m.key][i]
          entry[`ma7_${m.key}`] = normalize(ma7Map[m.key][i], m.key)
        }
        if (showMa14 && ma14Map[m.key]) {
          entry[`raw_ma14_${m.key}`] = ma14Map[m.key][i]
          entry[`ma14_${m.key}`] = normalize(ma14Map[m.key][i], m.key)
        }
      }
      return entry
    })

    return { chartData: rows, ranges: rangeMap }
  }, [sortedAsc, metrics, showMa7, showMa14])

  if (sortedAsc.length === 0) return null

  const activeMetrics = metrics.filter((m) => selected.has(m.key))

  return (
    <Card className="p-4 space-y-3">
      <h3 className="text-sm font-medium">カスタムグラフ</h3>

      {/* メトリクス選択 */}
      <ReactSelect<MetricOption, true>
        isMulti
        options={METRIC_OPTIONS}
        value={selectedOptions}
        onChange={(v: MultiValue<MetricOption>) => setSelectedOptions([...v])}
        styles={metricSelectStyles}
        placeholder="指標を選択…"
        noOptionsMessage={() => 'なし'}
        menuPortalTarget={document.body}
        menuShouldScrollIntoView={false}
        menuPosition="fixed"
      />

      {/* グラフ */}
      <div className="h-52 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.3} />
            <XAxis dataKey="date" tickFormatter={formatTick} fontSize={10} />
            <YAxis hide domain={[0, 100]} />
            <Tooltip
              contentStyle={{ fontSize: 12, maxWidth: 260 }}
              labelFormatter={(label) =>
                typeof label === 'string' ? format(parseISO(label), 'yyyy/M/d HH:mm', { locale: ja }) : ''
              }
              content={({
                active,
                payload,
                label,
              }: {
                active?: boolean
                payload?: readonly { payload?: Record<string, number | string | null> }[]
                label?: string | number
              }) => {
                if (!active || !payload?.length || !label) return null
                const row = payload[0]?.payload as Record<string, number | string | null> | undefined
                if (!row) return null
                return (
                  <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
                    <div className="mb-1 font-medium">
                      {format(parseISO(label as string), 'yyyy/M/d HH:mm', { locale: ja })}
                    </div>
                    {activeMetrics.map((m) => {
                      const raw = row[`raw_${m.key}`]
                      if (raw === null || raw === undefined) return null
                      const r = ranges[m.key]
                      return (
                        <div key={m.key}>
                          <div className="flex justify-between gap-3">
                            <span style={{ color: m.color }}>{m.label}</span>
                            <span className="tabular-nums">
                              {typeof raw === 'number' ? raw.toFixed(1) : raw} {m.unit}
                              {r && (
                                <span className="text-muted-foreground ml-1">
                                  ({r.min.toFixed(1)}–{r.max.toFixed(1)})
                                </span>
                              )}
                            </span>
                          </div>
                          {showMa7 && row[`raw_ma7_${m.key}`] != null && (
                            <div className="flex justify-between gap-3 pl-2 text-muted-foreground">
                              <span>7日MA</span>
                              <span className="tabular-nums">
                                {(row[`raw_ma7_${m.key}`] as number).toFixed(1)} {m.unit}
                              </span>
                            </div>
                          )}
                          {showMa14 && row[`raw_ma14_${m.key}`] != null && (
                            <div className="flex justify-between gap-3 pl-2 text-muted-foreground">
                              <span>14日MA</span>
                              <span className="tabular-nums">
                                {(row[`raw_ma14_${m.key}`] as number).toFixed(1)} {m.unit}
                              </span>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              }}
            />
            {activeMetrics.map((m) => (
              <Line
                key={m.key}
                type="monotone"
                dataKey={m.key}
                stroke={m.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 3 }}
                connectNulls
              />
            ))}
            {showMa7 &&
              activeMetrics.map((m) => (
                <Line
                  key={`ma7_${m.key}`}
                  type="monotone"
                  dataKey={`ma7_${m.key}`}
                  stroke={m.color}
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                  strokeOpacity={0.6}
                  dot={false}
                  connectNulls
                />
              ))}
            {showMa14 &&
              activeMetrics.map((m) => (
                <Line
                  key={`ma14_${m.key}`}
                  type="monotone"
                  dataKey={`ma14_${m.key}`}
                  stroke={m.color}
                  strokeWidth={1.5}
                  strokeDasharray="6 3"
                  strokeOpacity={0.4}
                  dot={false}
                  connectNulls
                />
              ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 凡例: 最新値 */}
      {activeMetrics.length > 0 && chartData.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {activeMetrics.map((m) => {
            const lastRow = chartData[chartData.length - 1]
            const raw = lastRow[`raw_${m.key}`]
            const latest = typeof raw === 'number' ? raw : null
            return (
              <span key={m.key} style={{ color: m.color }}>
                {m.label}: {latest !== null ? latest.toFixed(1) : '—'}
                {m.unit}
              </span>
            )
          })}
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// 体重派生グラフ（除脂肪体重・7日/14日移動平均）
// ---------------------------------------------------------------------------

const LbmChart = ({
  sortedAsc,
  showMa7,
  showMa14,
}: {
  sortedAsc: EufyMeasurementItem[]
  showMa7: boolean
  showMa14: boolean
}) => {
  const lbmData = useMemo(() => {
    return sortedAsc.map((m) => {
      const w = m.weight_kg as number | null
      const bf = m.body_fat as number | null
      return {
        date: m.measured_at as string,
        value: w !== null && bf !== null ? w * (1 - bf / 100) : null,
      }
    })
  }, [sortedAsc])

  return (
    <MetricChart
      data={lbmData}
      metric={{ key: 'lbm', label: '除脂肪体重', unit: 'kg', color: 'hsl(160 70% 40%)' }}
      showMa7={showMa7}
      showMa14={showMa14}
    />
  )
}

// ---------------------------------------------------------------------------
// 個別メトリクスカード
// ---------------------------------------------------------------------------

const MetricChart = ({
  data,
  metric,
  showMa7 = false,
  showMa14 = false,
}: {
  data: { date: string; value: number | null }[]
  metric: (typeof METRICS)[number]
  showMa7?: boolean
  showMa14?: boolean
}) => {
  const chartData = useMemo(() => {
    const values = data.map((d) => d.value)
    const ma7 = showMa7 ? computeMovingAverage(values, 7) : []
    const ma14 = showMa14 ? computeMovingAverage(values, 14) : []
    return data.map((d, i) => ({
      ...d,
      ...(showMa7 ? { ma7: ma7[i] } : {}),
      ...(showMa14 ? { ma14: ma14[i] } : {}),
    }))
  }, [data, showMa7, showMa14])

  const filtered = chartData.filter((d) => d.value !== null)
  if (filtered.length === 0) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-medium mb-2">{metric.label}</h3>
        <p className="text-xs text-muted-foreground">データなし</p>
      </Card>
    )
  }

  const values = filtered.map((d) => d.value as number)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const latest = values[values.length - 1]
  const first = values[0]
  const diff = latest - first
  const diffStr = diff >= 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1)

  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium">{metric.label}</h3>
        <span className="text-xs text-muted-foreground">
          {min.toFixed(1)} - {max.toFixed(1)} {metric.unit}
        </span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold tabular-nums">
          {latest.toFixed(1)}
          <span className="text-sm text-muted-foreground ml-1">{metric.unit}</span>
        </span>
        <span
          className={`text-xs ${diff > 0 ? 'text-orange-600' : diff < 0 ? 'text-green-600' : 'text-muted-foreground'}`}
        >
          {diffStr} {metric.unit}
        </span>
      </div>
      <div className="h-32 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={filtered}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.3} />
            <XAxis dataKey="date" tickFormatter={formatTick} fontSize={10} />
            <YAxis fontSize={10} domain={['auto', 'auto']} />
            <Tooltip
              contentStyle={{ fontSize: 12 }}
              labelFormatter={(label) =>
                typeof label === 'string' ? format(parseISO(label), 'yyyy/M/d HH:mm', { locale: ja }) : ''
              }
              formatter={(value, name) => {
                const labels: Record<string, string> = { value: metric.label, ma7: '7日MA', ma14: '14日MA' }
                return [
                  typeof value === 'number' ? `${value.toFixed(2)} ${metric.unit}` : '—',
                  labels[String(name)] ?? metric.label,
                ]
              }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={metric.color}
              strokeWidth={2}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
              connectNulls
              name="value"
            />
            {showMa7 && (
              <Line
                type="monotone"
                dataKey="ma7"
                stroke="hsl(35 90% 50%)"
                strokeWidth={1.5}
                strokeDasharray="4 2"
                dot={false}
                connectNulls
                name="ma7"
              />
            )}
            {showMa14 && (
              <Line
                type="monotone"
                dataKey="ma14"
                stroke="hsl(270 60% 55%)"
                strokeWidth={1.5}
                strokeDasharray="6 3"
                dot={false}
                connectNulls
                name="ma14"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}

export const EufyPage = () => {
  const { measurements, isMeasurementsLoading, fetchMeasurements, status, checkStatus } = useEufyStore()
  const [days, setDays] = useState(30)
  const [showMa7, setShowMa7] = useState(false)
  const [showMa14, setShowMa14] = useState(false)
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [customerName, setCustomerName] = useState<string | null>(null)
  const goBack = useGoBack()

  useEffect(() => {
    void checkStatus()
    getBodyProfile()
      .then((p) => setCustomerId(p.eufy_customer_id))
      .catch(() => null)
  }, [checkStatus])

  useEffect(() => {
    if (!customerId) return
    const start = subDays(new Date(), days).toISOString()
    void fetchMeasurements({ customer_id: customerId, start, limit: 2000 })
  }, [days, fetchMeasurements, customerId])

  // customer名を解決
  useEffect(() => {
    if (!customerId || !status?.customers) return
    const customer = status.customers.find((c) => c.customer_id === customerId)
    setCustomerName(customer?.name ?? null)
  }, [customerId, status?.customers])

  // 古い順にソート
  const sortedAsc = useMemo(() => {
    return [...measurements]
      .filter((m) => m.measured_at !== null)
      .sort((a, b) => new Date(a.measured_at as string).getTime() - new Date(b.measured_at as string).getTime())
  }, [measurements])

  if (!status) {
    return (
      <div className="flex h-dvh flex-col bg-background">
        <Header title="体重計データ" onClose={goBack} />
        <div className="p-4">
          <Skeleton className="h-32" />
        </div>
      </div>
    )
  }

  if (!status.authenticated) {
    return (
      <div className="flex h-dvh flex-col bg-background">
        <Header title="体重計データ" onClose={goBack} />
        <div className="flex-1 p-4">
          <Card className="p-6 text-center space-y-3">
            <h2 className="text-lg font-semibold">Eufy 未接続</h2>
            <p className="text-sm text-muted-foreground">体重計を見るには連携設定からログインしてください。</p>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-background">
      <Header title={`体重計データ${customerName ? ` - ${customerName}` : ''}`} onClose={goBack} />
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={showMa7}
                onChange={(e) => setShowMa7(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-[hsl(35_90%_50%)]">7日MA</span>
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={showMa14}
                onChange={(e) => setShowMa14(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-[hsl(270_60%_55%)]">14日MA</span>
            </label>
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => (
                <SelectItem key={r.days} value={String(r.days)}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isMeasurementsLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-72" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-48" />
              ))}
            </div>
          </div>
        ) : sortedAsc.length === 0 ? (
          <Card className="p-6 text-center text-muted-foreground">指定期間のデータがありません</Card>
        ) : (
          <>
            <OverlayChart sortedAsc={sortedAsc} metrics={METRICS} showMa7={showMa7} showMa14={showMa14} />
            <BfProjectionChart days={days} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {METRICS.filter((m) => m.key !== 'lbm').map((metric) => {
                const data = sortedAsc.map((m) => ({
                  date: m.measured_at as string,
                  value: (m[metric.key as keyof EufyMeasurementItem] as number | null) ?? null,
                }))
                return (
                  <MetricChart key={metric.key} data={data} metric={metric} showMa7={showMa7} showMa14={showMa14} />
                )
              })}
              <LbmChart sortedAsc={sortedAsc} showMa7={showMa7} showMa14={showMa14} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
